import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/docs/plugins/sdk-reference")({
	component: SdkReferencePage,
});

function H({ id, children }: { id: string; children: React.ReactNode }) {
	return (
		<h2
			id={id}
			className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mt-14 mb-4 pt-6 border-t border-zinc-100 dark:border-zinc-800/50 scroll-mt-20 first:border-0 first:mt-0 first:pt-0"
		>
			{children}
		</h2>
	);
}

function H3({ children }: { children: React.ReactNode }) {
	return (
		<h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mt-6 mb-2">
			{children}
		</h3>
	);
}

function Pre({ title, children }: { title?: string; children: string }) {
	return (
		<div className="rounded bg-[#0a0a0a] border border-zinc-800 overflow-hidden mb-4">
			{title && (
				<div className="px-3 py-1 text-[9px] text-zinc-500 uppercase tracking-widest border-b border-zinc-800 font-medium">
					{title}
				</div>
			)}
			<pre className="px-3 py-2 overflow-x-auto text-[11px] leading-relaxed text-zinc-300 font-mono">
				{children}
			</pre>
		</div>
	);
}

function DocTable({
	headers,
	children,
}: { headers: string[]; children: React.ReactNode }) {
	return (
		<div className="rounded border border-zinc-200 dark:border-zinc-800 mb-4 text-[12px]">
			<Table>
				<TableHeader>
					<TableRow className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50">
						{headers.map((h) => (
							<TableHead
								key={h}
								className="text-left py-1.5 px-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium h-auto"
							>
								{h}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody className="divide-y divide-zinc-100 dark:divide-zinc-800/50">
					{children}
				</TableBody>
			</Table>
		</div>
	);
}

function SdkReferencePage() {
	const { t } = useTranslation("docs");
	return (
		<div className="w-full">
			<div className="mb-4">
				<h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
					{t("sdk_title")}
				</h1>
				<p
					className="mt-1.5 text-[13px] text-zinc-500"
					dangerouslySetInnerHTML={{ __html: t("sdk_intro") }}
				/>
			</div>

			<H id="macros">{t("sdk_macros")}</H>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				{t("sdk_macros_desc")}
			</p>

			<H3>
				<code className="text-[11px] font-mono">extraction_handler!</code>
			</H3>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				Extension point:{" "}
				<code className="text-[11px] font-mono">enrichment.extract</code>
			</p>
			<Pre>{`extraction_handler!(handler_name, |input: EnrichmentInput| {
    ExtractResult { confidence: 0.9, action: "extracted".into(), extracted: json!({...}), metadata: None }
});`}</Pre>

			<H3>
				<code className="text-[11px] font-mono">calculation_handler!</code>
			</H3>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				Extension point:{" "}
				<code className="text-[11px] font-mono">enrichment.calculate</code>
			</p>
			<Pre>{`calculation_handler!(handler_name, |input: EnrichmentInput| {
    ExtractResult { confidence: 0.99, action: "extracted".into(), extracted: json!({...}), metadata: None }
});`}</Pre>

			<H3>
				<code className="text-[11px] font-mono">pipeline_step_handler!</code>
			</H3>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				Extension point:{" "}
				<code className="text-[11px] font-mono">pipeline.step</code>
			</p>
			<Pre>{`pipeline_step_handler!(handler_name, |input: PipelineStepInput| {
    PipelineStepOutput {
        success: true,
        result: json!({...}),
        logs: vec![],
        next_step: None,
        progress_pct: Some(100),
    }
});`}</Pre>

			<H3>
				<code className="text-[11px] font-mono">event_handler!</code>
			</H3>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				Extension point:{" "}
				<code className="text-[11px] font-mono">event.handler</code>
			</p>
			<Pre>{`event_handler!(handler_name, |input: EventHandlerInput| {
    EventHandlerOutput {
        handled: true,
        actions: vec![EventAction::Log { level: "info".into(), message: "done".into() }],
    }
});`}</Pre>

			<H id="types-enrichment">{t("sdk_types_enrichment")}</H>

			<H3>EnrichmentInput</H3>
			<DocTable headers={[t("sdk_field"), t("sdk_type"), t("sdk_description")]}>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							workspace_id
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">Uuid</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_ei_workspace")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							product_id
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">Uuid</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_ei_product")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							title
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">String</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_ei_title")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							description
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{"Option<String>"}</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("sdk_ei_description")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							properties
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">Value</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("sdk_ei_properties")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							mode
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">String</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						<code className="text-[11px] font-mono">"fast"</code> /{" "}
						<code className="text-[11px] font-mono">"accurate"</code>
					</TableCell>
				</TableRow>
			</DocTable>

			<H3>ExtractResult</H3>
			<DocTable headers={[t("sdk_field"), t("sdk_type"), t("sdk_description")]}>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							confidence
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">f32</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">0.0–1.0</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							action
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">String</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						<code className="text-[11px] font-mono">"extracted"</code>,{" "}
						<code className="text-[11px] font-mono">"not_applicable"</code>,{" "}
						<code className="text-[11px] font-mono">"error"</code>
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							extracted
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">Value</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_er_extracted")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							metadata
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{"Option<Value>"}</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_er_metadata")}</TableCell>
				</TableRow>
			</DocTable>

			<H id="types-pipeline">{t("sdk_types_pipeline")}</H>

			<H3>PipelineStepInput</H3>
			<DocTable headers={[t("sdk_field"), t("sdk_type"), t("sdk_description")]}>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							run_id
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">Uuid</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_psi_run_id")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							step_name
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">String</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("sdk_psi_step_name")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							workspace_id
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">Uuid</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("sdk_psi_workspace")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							step_index
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">usize</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("sdk_psi_step_index")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							context
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">Value</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_psi_context")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							config
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">Value</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_psi_config")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							previous_outputs
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{"Vec<Value>"}</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_psi_previous")}</TableCell>
				</TableRow>
			</DocTable>

			<H3>PipelineStepOutput</H3>
			<DocTable headers={[t("sdk_field"), t("sdk_type"), t("sdk_description")]}>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							success
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">bool</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_pso_success")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							result
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">Value</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_pso_result")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							logs
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{"Vec<String>"}</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_pso_logs")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							next_step
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{"Option<String>"}</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("sdk_pso_next_step")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							progress_pct
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{"Option<i32>"}</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_pso_progress")}</TableCell>
				</TableRow>
			</DocTable>

			<H id="types-events">{t("sdk_types_events")}</H>

			<H3>EventHandlerInput</H3>
			<DocTable headers={[t("sdk_field"), t("sdk_type"), t("sdk_description")]}>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							event_type
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">String</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						<code className="text-[11px] font-mono">"order.created"</code>,{" "}
						<code className="text-[11px] font-mono">"product.updated"</code>...
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							payload
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">Value</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_ehi_payload")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							metadata
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">EventMetadata</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						event_id, workspace_id, entity_id, timestamp, source, subject
					</TableCell>
				</TableRow>
			</DocTable>

			<H3>EventAction (enum)</H3>
			<DocTable
				headers={[
					t("sdk_ea_variant"),
					t("sdk_ea_fields"),
					t("sdk_description"),
				]}
			>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							PublishEvent
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						event_type, entity_id, payload
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_ea_publish")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							ScheduleTask
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						task_type, delay_seconds, payload
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_ea_schedule")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							Log
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">level, message</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_ea_log")}</TableCell>
				</TableRow>
			</DocTable>

			<H id="host-functions">{t("sdk_host_functions")}</H>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("sdk_host_functions_desc") }}
			/>

			<DocTable
				headers={[
					t("sdk_host_function"),
					t("sdk_host_capability"),
					t("sdk_description"),
				]}
			>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							db_query(sql, params)
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">db_read</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("sdk_host_db_query")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							db_execute(sql, params)
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">db_write</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("sdk_host_db_execute")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							kv_get(key)
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">kv</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_host_kv_get")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							kv_set(key, value)
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">kv</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_host_kv_set")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							kv_set_with_ttl(key, value, ttl)
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">kv</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("sdk_host_kv_set_ttl")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							kv_delete(key)
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">kv</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("sdk_host_kv_delete")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							ref_data(domain, key)
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">ref_data</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("sdk_host_ref_data")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							log_info / log_error / log_warn
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-400">{"\u2014"}</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_host_log")}</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							time_now()
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-400">{"\u2014"}</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">Unix millis</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							uuid_v4()
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-400">{"\u2014"}</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">{t("sdk_host_uuid")}</TableCell>
				</TableRow>
			</DocTable>

			<H id="error-handling">{t("sdk_error_handling")}</H>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("sdk_error_handling_desc") }}
			/>
			<Pre>{`extraction_handler!(my_handler, |input: EnrichmentInput| {
    match do_extract(&input) {
        Ok(data) => ExtractResult {
            confidence: 0.9, action: "extracted".into(), extracted: data, metadata: None,
        },
        Err(e) => {
            log_error(&format!("Failed: {e}"));
            ExtractResult {
                confidence: 0.0, action: "error".into(), extracted: json!({}),
                metadata: Some(json!({"error": e.to_string()})),
            }
        }
    }
});`}</Pre>

			<div className="mt-14 pt-4 border-t border-zinc-100 dark:border-zinc-800/50 text-center">
				<p className="text-[10px] text-zinc-400 tracking-wider uppercase">
					OmniOS Plugin SDK Reference
				</p>
			</div>
		</div>
	);
}
